<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { isDrawnOn, canvasToBlob, clearCanvas } from '@/core/utils/signature'

const props = defineProps({
  width: {
    type: Number,
    default: 400,
  },
  height: {
    type: Number,
    default: 150,
  },
  strokeColor: {
    type: String,
    default: '#000000',
  },
  strokeWidth: {
    type: Number,
    default: 2,
  },
})

const emit = defineEmits(['update:hasContent'])

const canvasRef = ref(null)
let ctx = null
let isDrawing = false
let lastX = 0
let lastY = 0

function getCoordinates(e) {
  const rect = canvasRef.value.getBoundingClientRect()
  const scaleX = canvasRef.value.width / rect.width
  const scaleY = canvasRef.value.height / rect.height

  if (e.touches && e.touches.length > 0) {
    return {
      x: (e.touches[0].clientX - rect.left) * scaleX,
      y: (e.touches[0].clientY - rect.top) * scaleY,
    }
  }

  return {
    x: (e.clientX - rect.left) * scaleX,
    y: (e.clientY - rect.top) * scaleY,
  }
}

function startDrawing(e) {
  e.preventDefault()
  isDrawing = true
  const coords = getCoordinates(e)
  lastX = coords.x
  lastY = coords.y
  ctx.beginPath()
  ctx.moveTo(lastX, lastY)
}

function draw(e) {
  if (!isDrawing) return
  e.preventDefault()

  const coords = getCoordinates(e)
  ctx.lineTo(coords.x, coords.y)
  ctx.stroke()
  lastX = coords.x
  lastY = coords.y
}

function stopDrawing() {
  if (isDrawing) {
    isDrawing = false
    ctx.closePath()
    emit('update:hasContent', hasSignature())
  }
}

function initCanvas() {
  if (!canvasRef.value) return

  ctx = canvasRef.value.getContext('2d')
  ctx.strokeStyle = props.strokeColor
  ctx.lineWidth = props.strokeWidth
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
}

function clear() {
  clearCanvas(canvasRef.value)
  emit('update:hasContent', false)
}

function hasSignature() {
  return isDrawnOn(canvasRef.value)
}

async function getBlob() {
  if (!hasSignature()) return null
  return canvasToBlob(canvasRef.value)
}

function getCanvas() {
  return canvasRef.value
}

// Expose methods for parent components
defineExpose({
  clear,
  hasSignature,
  getBlob,
  getCanvas,
})

onMounted(() => {
  initCanvas()

  // Add event listeners
  const canvas = canvasRef.value
  canvas.addEventListener('mousedown', startDrawing)
  canvas.addEventListener('mousemove', draw)
  canvas.addEventListener('mouseup', stopDrawing)
  canvas.addEventListener('mouseleave', stopDrawing)

  // Touch events
  canvas.addEventListener('touchstart', startDrawing, { passive: false })
  canvas.addEventListener('touchmove', draw, { passive: false })
  canvas.addEventListener('touchend', stopDrawing)
  canvas.addEventListener('touchcancel', stopDrawing)
})

onUnmounted(() => {
  const canvas = canvasRef.value
  if (!canvas) return

  canvas.removeEventListener('mousedown', startDrawing)
  canvas.removeEventListener('mousemove', draw)
  canvas.removeEventListener('mouseup', stopDrawing)
  canvas.removeEventListener('mouseleave', stopDrawing)
  canvas.removeEventListener('touchstart', startDrawing)
  canvas.removeEventListener('touchmove', draw)
  canvas.removeEventListener('touchend', stopDrawing)
  canvas.removeEventListener('touchcancel', stopDrawing)
})
</script>

<template>
  <div class="signature-pad-wrapper">
    <div class="signature-pad-container">
      <canvas ref="canvasRef" :width="width" :height="height" class="signature-canvas"></canvas>
      <div class="signature-line"></div>
    </div>
    <button type="button" class="clear-btn" @click="clear">
      <span class="material-symbols-rounded">refresh</span>
      נקה
    </button>
  </div>
</template>

<style scoped>
.signature-pad-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.signature-pad-container {
  position: relative;
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--color-white);
  overflow: hidden;
}

.signature-canvas {
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  cursor: crosshair;
  touch-action: none;
}

.signature-line {
  position: absolute;
  bottom: 30%;
  left: 10%;
  right: 10%;
  height: 1px;
  background: var(--border-color);
  pointer-events: none;
}

.clear-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  align-self: flex-start;
  padding: var(--spacing-xs) var(--spacing-sm);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.clear-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.clear-btn .material-symbols-rounded {
  font-size: 16px;
}
</style>
